import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { useAuth } from "@/auth/AuthProvider";
import { PlaybookTemplate } from "@/lib/learn/playbookRegistry";
import { PlaybookService } from "@/services/learn/PlaybookService";
import { brandAssets } from "@/lib/brand/tokens";

interface PlaybookCardProps {
  playbook: PlaybookTemplate;
}

const CATEGORY_LABEL: Record<string, string> = {
  growth: "Growth",
  support: "Support",
  operations: "Operations",
  quality: "Quality",
  brand: "Brand",
  "getting-started": "Getting Started",
  knowledge: "Knowledge",
  integrations: "Integrations",
  analytics: "Analytics",
};

// Wash a hex colour with alpha so the playbook cover gradient is a soft
// pastel tint rather than a full-saturation block — keeps the navy wordmark
// readable while preserving each playbook's accent identity.
const hexToRgba = (hex: string, alpha: number) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const PlaybookCard = ({ playbook }: PlaybookCardProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { userProfile } = useAuth();
  const { data: agents } = useUserAgents(1);
  const primaryGenie = agents?.[0];

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const recipient = userProfile?.full_name || userProfile?.company_name || "";
      const primaryColor =
        (primaryGenie?.branding as { primaryColor?: string } | undefined)?.primaryColor ||
        playbook.coverAccent;
      const ok = await PlaybookService.download(playbook.slug, {
        recipientName: recipient,
        primaryGenieName: primaryGenie?.name,
        primaryColor,
      });
      if (ok) {
        toast.success("Your playbook is downloading.");
      } else {
        toast.error("We couldn't prepare that playbook. Try again.");
      }
    } catch (error) {
      console.error("[PlaybookCard] download error:", error);
      toast.error("We couldn't prepare that playbook. Try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const accent = playbook.coverAccent || "#4E9CFF";
  const categoryLabel = CATEGORY_LABEL[playbook.category] ?? playbook.category;

  return (
    <div className="group relative flex flex-col rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-genie-primary/30 transition-all duration-200">
      {/* Cover — pastel signature gradient tinted from the playbook's accent
          colour. Soft enough that the navy Help Genie wordmark reads cleanly,
          while still distinguishing each playbook by hue. */}
      <div
        className="h-24 relative overflow-hidden flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(accent, 0.28)} 0%, ${hexToRgba("#FF4DB8", 0.22)} 100%), #FFFFFF`,
        }}
      >
        <img
          src={brandAssets.wordmark}
          alt="Help Genie"
          className="h-8 w-auto"
        />

        {/* Top-left category badge */}
        <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/85 text-genie-text shadow-sm backdrop-blur-sm">
          {categoryLabel}
        </span>
      </div>

      {/* Body */}
      <div className="p-3.5 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-genie-text mb-1 line-clamp-2">
          {playbook.title}
        </h3>
        <p className="text-xs text-gray-600 mb-3 flex-1 line-clamp-2">{playbook.description}</p>
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
          <span className="text-[11px] text-gray-500">{playbook.estReadMinutes}-min read</span>
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            size="sm"
            className="h-7 px-2.5 text-xs bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white shadow-sm hover:shadow-md transition-all"
          >
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" />
            )}
            Download
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlaybookCard;
